/**
 * Delegation Store — Zustand state for the delegation helper feature.
 *
 * Manages: active delegation grant context, editing lock state, toolbar state,
 * idle timer, and the helper's delegated application list.
 *
 * Path: src/stores/delegationStore.ts
 */
import { create } from 'zustand';

export type ToolbarState = 'ACTIVE' | 'IDLE_WARNING' | 'CRITICAL' | 'RELEASED' | 'INACTIVE';

export interface DelegationGrant {
    grantId: string;
    helperId: string;
    helperName: string;
    helperEmail: string;
    applicantId: string;
    applicantName: string;
    applicantEmail: string;
    applicationId: string;
    scope: 'PRIMARY_APPLICANT' | 'PARTICIPANT';
    participantId?: string;
    status: string;
    permissions: string;
    grantedAt: string;
    expiresAt: string;
}

export interface LockHolder {
    userId: string;
    userName: string;
    role: 'HELPER' | 'APPLICANT';
    grantId?: string;
    acquiredAt: string;
    lastRealActivityAt: string;
}

interface DelegationState {
    // ── Active delegation context ────────────────────────────────────────
    activeGrant: DelegationGrant | null;
    isActive: boolean;

    // ── Editing lock ─────────────────────────────────────────────────────
    lockAcquired: boolean;
    lockHolder: LockHolder | null;
    sessionMinutes: number;

    // ── Toolbar ──────────────────────────────────────────────────────────
    toolbarState: ToolbarState;
    idleCountdownSeconds: number;

    // ── Activity tracking ────────────────────────────────────────────────
    lastRealActivityAt: number; // epoch ms
    lastPassivePresenceAt: number; // epoch ms

    // ── Helper dashboard grants ──────────────────────────────────────────
    helperGrants: DelegationGrant[];

    // ── Actions ──────────────────────────────────────────────────────────
    enterDelegation: (grant: DelegationGrant) => void;
    exitDelegation: () => void;
    setLockAcquired: (acquired: boolean, holder?: LockHolder) => void;
    setLockHolder: (holder: LockHolder | null) => void;
    setToolbarState: (state: ToolbarState) => void;
    setIdleCountdownSeconds: (seconds: number) => void;
    recordRealActivity: () => void;
    recordPassivePresence: () => void;
    setSessionMinutes: (minutes: number) => void;
    setHelperGrants: (grants: DelegationGrant[]) => void;
}

export const useDelegationStore = create<DelegationState>()((set) => ({
    activeGrant: null,
    isActive: false,
    lockAcquired: false,
    lockHolder: null,
    sessionMinutes: 0,
    toolbarState: 'INACTIVE',
    idleCountdownSeconds: 0,
    lastRealActivityAt: 0,
    lastPassivePresenceAt: 0,
    helperGrants: [],

    enterDelegation: (grant) =>
        set({
            activeGrant: grant,
            isActive: true,
            toolbarState: 'ACTIVE',
            lastRealActivityAt: Date.now(),
            lastPassivePresenceAt: Date.now(),
        }),

    exitDelegation: () => {
        sessionStorage.removeItem('delegation_grantId');
        sessionStorage.removeItem('delegation_helperId');
        sessionStorage.removeItem('delegation_appId');
        set({
            activeGrant: null,
            isActive: false,
            lockAcquired: false,
            lockHolder: null,
            toolbarState: 'INACTIVE',
            idleCountdownSeconds: 0,
            sessionMinutes: 0,
            lastRealActivityAt: 0,
            lastPassivePresenceAt: 0,
        });
    },

    setLockAcquired: (acquired, holder) =>
        set({ lockAcquired: acquired, lockHolder: holder ?? null }),

    setLockHolder: (holder) =>
        set({ lockHolder: holder }),

    setToolbarState: (state) =>
        set({ toolbarState: state }),

    setIdleCountdownSeconds: (seconds) =>
        set({ idleCountdownSeconds: seconds }),

    recordRealActivity: () =>
        set({
            lastRealActivityAt: Date.now(),
            toolbarState: 'ACTIVE',
            idleCountdownSeconds: 0,
        }),

    recordPassivePresence: () =>
        set({ lastPassivePresenceAt: Date.now() }),

    setSessionMinutes: (minutes) =>
        set({ sessionMinutes: minutes }),

    setHelperGrants: (grants) =>
        set({ helperGrants: grants }),
}));
